/**
 * Keep a canvas's backing store matched to its laid-out size.
 *
 * Measuring `window.innerWidth` once at mount and then only on the `resize`
 * event is not enough, and it fails in a way that is easy to misread as "the
 * renderer is broken": if the element has no size yet when the effect runs —
 * a hidden tab, a pane that is sized after mount, a deferred layout — the
 * backing store is created at 0 (clamped to 1x1) and nothing ever fires to
 * correct it. The canvas then stretches one pixel across the whole screen while
 * every other check passes: WebGL is fine, the shader compiled, the draw calls
 * run. That happened here.
 *
 * A ResizeObserver on the element itself is measured from the thing that
 * actually has the size, and fires whenever that changes for any reason.
 */

export type SizeHandler = (cssW: number, cssH: number, dpr: number) => void;

/**
 * Observe `canvas` and call `onSize` whenever its laid-out size changes, plus
 * once immediately. Returns a disposer.
 *
 * Zero sizes are swallowed rather than passed through — a renderer cannot do
 * anything useful with them, and forwarding them is what creates the 1x1 state.
 */
export function observeCanvasSize(canvas: HTMLCanvasElement, onSize: SizeHandler): () => void {
  let lastW = -1;
  let lastH = -1;
  let lastDpr = -1;

  const measure = () => {
    const rect = canvas.getBoundingClientRect();
    // Fall back to the viewport for a canvas that is display:none or not yet
    // laid out but will be; better an approximately right buffer than none.
    const w = Math.round(rect.width || window.innerWidth || 0);
    const h = Math.round(rect.height || window.innerHeight || 0);
    const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
    if (w <= 0 || h <= 0) return;
    if (w === lastW && h === lastH && dpr === lastDpr) return;
    lastW = w;
    lastH = h;
    lastDpr = dpr;
    onSize(w, h, dpr);
  };

  measure();

  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(measure);
    ro.observe(canvas);
  }
  // devicePixelRatio changes (moving between displays) do not trigger a
  // ResizeObserver, and older Safari has no ResizeObserver at all.
  window.addEventListener('resize', measure);
  window.addEventListener('orientationchange', measure);

  return () => {
    ro?.disconnect();
    window.removeEventListener('resize', measure);
    window.removeEventListener('orientationchange', measure);
  };
}
